import React from 'react'
import { useInterval } from 'react-use'

// Constants
const TERMLY_LINK_CLASS_NAME = 'termly-display-preferences'
const TERMLY_WINDOW_LOADED_PROPERTY = 'TERMLY_RESOURCE_BLOCKER_LOADED'
const TERMLY_LINK_SELECTOR = `.${TERMLY_LINK_CLASS_NAME}`
const TERMLY_SCRIPT_ID = 'termly-script'
const TERMLY_SCRIPT_CHECK_TIMEOUT = 10000
const TERMLY_SCRIPT_CHECK_INTERVAL = 1000
const TERMLY_SCRIPT_LOAD_TIMEOUT = 1000

type TermlyScriptLoaderProps = Readonly<{
  renderLink: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => React.ReactNode
}>

export function TermlyScriptLoader({ renderLink }: TermlyScriptLoaderProps) {
  const [isTermlyReady, setIsTermlyReady] = React.useState(false)
  const [shouldCheck, setShouldCheck] = React.useState(true)

  React.useEffect(() => {
    // Check if Termly is already loaded
    if (document.getElementById(TERMLY_SCRIPT_ID) || TERMLY_WINDOW_LOADED_PROPERTY in window) {
      setIsTermlyReady(true)
      setShouldCheck(false)
      return
    }
    // Create and load the script
    const script = document.createElement('script')

    script.type = 'text/javascript'
    script.id = TERMLY_SCRIPT_ID
    script.src = import.meta.env.VITE_TERMLY_SCRIPT_SRC
    script.async = true
    // Handle script load success
    script.onload = () => {
      // Wait a bit for Termly to initialize
      setTimeout(() => {
        // Check if Termly is actually functional
        if (document.querySelector(TERMLY_LINK_SELECTOR)) {
          setIsTermlyReady(true)
          setShouldCheck(false)
        }
      }, TERMLY_SCRIPT_LOAD_TIMEOUT)
    }
    // Handle script load error
    script.onerror = () => {
      console.error('Failed to load Termly script')
      setShouldCheck(false)
    }

    document.body.appendChild(script)

    // Stop checking after 10 seconds
    const timeout = setTimeout(() => {
      setShouldCheck(false)
    }, TERMLY_SCRIPT_CHECK_TIMEOUT)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  // Use useInterval hook for periodic checking
  useInterval(
    () => {
      // Check multiple indicators that Termly is ready
      if (
        TERMLY_WINDOW_LOADED_PROPERTY in window ||
        document.querySelector(TERMLY_LINK_SELECTOR) ||
        document.getElementById(TERMLY_SCRIPT_ID) ||
        (window as any).Termly
      ) {
        setIsTermlyReady(true)
        setShouldCheck(false)
      }
    },
    shouldCheck ? TERMLY_SCRIPT_CHECK_INTERVAL : null,
  )

  // Only render when Termly is ready
  if (!isTermlyReady) {
    return null
  }

  return renderLink({ className: TERMLY_LINK_CLASS_NAME, href: '#' })
}
